import React, { useEffect, useState, useCallback, useMemo } from "react";
import TwilioWhatsAppChat from "./TwilioWhatsAppChat";
import userResources from "api/userResources";
import PhoneInputWithCountrySelect from "react-phone-number-input";
import { FaSearch, FaBars, FaTimes } from "react-icons/fa";
import { useForm } from "react-hook-form";
import debounce from "lodash.debounce";
import { avtar } from "utils/images";
import { motion, AnimatePresence } from "framer-motion";
import { clearWhatsAppMessages, selectWhatsAppMessages } from "../../../redux/whatsAppSlice";
import { useDispatch, useSelector } from "react-redux";

export default function CustomerNumberList() {
  const messages = useSelector(selectWhatsAppMessages);
  const dispatch = useDispatch();
  console.log(messages);

  const { setValue, watch } = useForm({
    defaultValues: { phone_number: "" },
  });
  const [selectedCustomer, setSelectedCustomer] = useState();
  const [customerData, setCustomerData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  // Pagination: pageIndex is zero-indexed; pageSize is fixed.
  const [pagination, setPagination] = useState({
    pageIndex: 1,
    pageSize: 10,
  });
  // "hasMore" tracks if more pages are available.
  const [hasMore, setHasMore] = useState(true);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const phoneNumber = watch("phone_number");
  const [refreshSelectedCustomerData, setRefreshSelectedCustomerData] =
    useState(false);
  // console.log(refreshSelectedCustomerData, "refreshSelectedCustomerData");

  // Fetch customer data.
  const getCustomer = useCallback(async (pageIndex, pageSize, phone_number) => {
    const params = {
      page: pageIndex,
      limit: pageSize,
      phone_number: phone_number || null,
    };
    setIsLoading(true);
    try {
      const response = await userResources.customerList(params);
      const newData = response.data.data;
      setCustomerData((prev) =>
        pageIndex === 0 ? newData : [...prev, ...newData]
      );

      const lastPage = response.data.paginate_data.last_page;
      setHasMore(pageIndex + 1 <= lastPage);
      dispatch(clearWhatsAppMessages());
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const debouncedGetCustomer = useMemo(
    () =>
      debounce(
        (pageIndex, pageSize, phone_number) =>
          getCustomer(pageIndex, pageSize, phone_number),
        300
      ),
    [getCustomer]
  );

  // Use a derived value like messages.length or a stringified version to detect changes
  useEffect(() => {
    // Use length or JSON.stringify if messages can mutate without changing reference
    debouncedGetCustomer(0, 10);
  }, [messages.length]); // or [JSON.stringify(messages)] if deep comparison needed

  // Reset pagination, customerData and hasMore when phone number (search term) changes.
  useEffect(() => {
    setPagination({ pageIndex: 1, pageSize: 10 });
    setCustomerData([]);
    setHasMore(true);
  }, [phoneNumber]);

  // Trigger fetching customer data when phoneNumber, pageIndex, or pageSize changes.
  useEffect(() => {
    debouncedGetCustomer(
      pagination.pageIndex,
      pagination.pageSize,
      phoneNumber
    );
    return () => {
      debouncedGetCustomer.cancel();
    };
  }, [
    phoneNumber,
    pagination.pageIndex,
    pagination.pageSize,
    debouncedGetCustomer,
  ]);

  const handleScroll = useCallback(
    debounce((e) => {
      if (!hasMore || isLoading) return;

      const { scrollTop, scrollHeight, clientHeight } = e.target;
      const threshold = 100;

      if (scrollTop + clientHeight >= scrollHeight - threshold) {
        setPagination((prev) => ({
          ...prev,
          pageIndex: prev.pageIndex + 1,
        }));
      }
    }, 300),
    [hasMore, isLoading]
  );
  useEffect(() => {
    return () => {
      handleScroll.cancel();
    };
  }, [handleScroll]);

  const listItemVariants = {
    hidden: { opacity: 0, x: -50 },
    show: { opacity: 1, x: 0 },
  };

  // refreshSelectedCustomer

  const refreshSelectedCustomer = async (value) => {
    setRefreshSelectedCustomerData(true);

    const params = {
      phone_number: value?.phone_number,
    };

    try {
      const response = await userResources.customerList(params);
      const newData = response?.data?.data;

      if (Array.isArray(newData) && newData.length > 0) {
        const matchedCustomer = newData.find(
          (item) => item.phone_number === value?.phone_number
        );

        if (matchedCustomer) {
          setSelectedCustomer(matchedCustomer);
        } else {
          console.warn("Matching customer not found in the data.");
        }
      } else {
        console.warn("No customer data returned.");
      }
    } catch (error) {
      console.error("Error fetching customer data:", error);
    }
  };

  // Reusable customer list content for mobile and desktop.
  const customerListContent = (
    <div className="bg-gray-900 p-2">
      <div className="relative w-full rounded-lg bg-white p-2">
        <PhoneInputWithCountrySelect
          defaultCountry="CA"
          value={phoneNumber}
          onChange={(value) => setValue("phone_number", value)}
          placeholder="Enter Phone Number"
          className="w-full rounded-md border p-2 text-lg focus:outline-none focus:ring-2 dark:!border-white/10"
        />
        <FaSearch className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-900" />
      </div>
      <div className="mt-2 h-[70vh] overflow-y-auto" onScroll={handleScroll}>
        {customerData.map((customer) => (
          <motion.div
            key={customer.id}
            className={`my-1 flex cursor-pointer items-center gap-2 rounded p-2 hover:bg-[#bb8e25] hover:text-white 
              ${selectedCustomer?.id === customer.id ? "bg-[#bb8e25]" : ""}`}
            onClick={() => {
              setValue("phone_number", "");
              setSelectedCustomer(customer);
              // Optionally close the mobile menu when a customer is selected
              setIsMobileMenuOpen(false);
            }}
            variants={listItemVariants}
            initial="hidden"
            animate="show"
          >
            <div className="tooltip" data-tip="Profile">
              <motion.img
                whileHover={{ scale: 1.1 }}
                className="h-10 w-12 cursor-pointer rounded-full"
                src={customer?.full_path || avtar}
                alt="Profile"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = avtar;
                }}
              />
            </div>
            <div className="w-full rounded-md bg-gray-800 p-3 text-white shadow-sm">
              <div className="flex flex-col gap-0.5">
                <div className="text-sm font-medium text-white">
                  {customer?.full_name}
                </div>
                <div className="text-sm text-gray-300">
                  {customer?.phone_number}
                </div>
                <div
                  className={`${
                    customer?.lastWhatsappMessage?.status === "delivered"
                      ? "text-red-500"
                      : "text-gray-400"
                  } mt-1 text-xs`}
                >
                  {customer?.lastWhatsappMessage?.content?.length > 40
                    ? customer.lastWhatsappMessage.content.slice(0, 40) + "..."
                    : customer?.lastWhatsappMessage?.content}
                </div>
              </div>
            </div>
          </motion.div>
        ))}
        <div className="flex items-center justify-center">
          {isLoading && (
            <span className="loading loading-spinner loading-sm text-[#bb8e25]"></span>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="relative">
      {/* Mobile header with hamburger */}
      <div className="sticky top-0 flex items-center justify-between bg-gray-900 p-2 text-white md:hidden">
        <button
          onClick={() => setIsMobileMenuOpen((prev) => !prev)}
          className="p-2 text-xl"
        >
          <FaBars />
        </button>
        <h1 className="text-lg font-bold uppercase text-[#bb8e25]">
          Select User
        </h1>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-6">
        {/* Mobile Drawer */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ x: "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: "-100%" }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              className="fixed left-0 top-0 z-50 h-full w-3/4 overflow-y-auto bg-gray-900 p-4 md:hidden"
            >
              <div className="mb-4 flex justify-end">
                <button
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="text-2xl text-white"
                >
                  <FaTimes />
                </button>
              </div>
              {customerListContent}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Desktop Customer List */}
        <div className="col-span-2 hidden md:block">{customerListContent}</div>

        {/* Chat Component */}
        <div className="col-span-1 md:col-span-4">
          <TwilioWhatsAppChat
            selectedCustomer={selectedCustomer}
            refreshSelectedCustomer={refreshSelectedCustomer}
          />
        </div>
      </div>
    </div>
  );
}
